import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { invoicesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { TruncatedTextWithTooltip } from '../components/TruncatedTextWithTooltip';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ListPagination } from '../components/ListPagination';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { SortableTableHead } from '../components/ui/SortableTableHead';
import { Plus, Search, Loader2, Receipt, RefreshCw, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { MobileFAB } from '../components/MobileFAB';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  { value: 'sent', label: 'Sent', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'partially_paid', label: 'Partial', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  { value: 'paid', label: 'Paid', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  { value: 'void', label: 'Void', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
];

function StatusBadge({ status }) {
  const opt = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
  return <Badge className={`${opt.color} text-[10px]`} data-testid={`status-badge-${status}`}>{opt.label}</Badge>;
}

export default function Invoices() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOwner, isOffice } = useAuth();
  const canEdit = isOwner || isOffice;

  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const search = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const sortBy = searchParams.get('sort') || null;
  const sortDir = searchParams.get('dir') || null;
  const pageSize = 25;

  const handleSort = useCallback((key) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      const curKey = p.get('sort');
      const curDir = p.get('dir');
      const DEFAULTS = { number: 'asc', status: 'asc', total: 'desc', due_date: 'asc', created: 'desc' };
      const defDir = DEFAULTS[key] || 'asc';
      if (curKey !== key) { p.set('sort', key); p.set('dir', defDir); }
      else if (curDir === defDir) { p.set('sort', key); p.set('dir', defDir === 'asc' ? 'desc' : 'asc'); }
      else { p.delete('sort'); p.delete('dir'); }
      p.set('page', '1');
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (sortBy) { params.sort_by = sortBy; params.sort_dir = sortDir; }
      const res = await invoicesAPI.list(params);
      setInvoices(res.data.invoices);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, sortBy, sortDir]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleSearch = (value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set('search', value); else p.delete('search');
    p.set('page', '1');
    setSearchParams(p);
  };

  const handleStatusFilter = (value) => {
    const p = new URLSearchParams(searchParams);
    if (value && value !== 'all') p.set('status', value); else p.delete('status');
    p.set('page', '1');
    setSearchParams(p);
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Layout>
      <div className="space-y-6" data-testid="invoices-page">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="invoices-title">
              <Receipt className="w-6 h-6" />
              Invoices
            </h1>
            <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
              {total} invoice{total !== 1 ? 's' : ''} — create, send, and track payments
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button onClick={() => navigate('/invoices/new')} data-testid="create-invoice-btn" className="hidden md:inline-flex">
                <Plus className="w-4 h-4 mr-2" /> New Invoice
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search invoices..." value={search} onChange={(e) => handleSearch(e.target.value)} className="pl-10 h-10" data-testid="invoice-search" />
          </div>
          {/* Mobile: filter icon */}
          <div className="sm:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" data-testid="invoice-mobile-filter-btn">
                  <Filter className={`w-4 h-4 ${statusFilter ? 'text-primary' : ''}`} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleStatusFilter('all')} className={!statusFilter ? 'font-semibold' : ''}>All Statuses</DropdownMenuItem>
                {STATUS_OPTIONS.map(s => (
                  <DropdownMenuItem key={s.value} onClick={() => handleStatusFilter(s.value)} className={statusFilter === s.value ? 'font-semibold' : ''}>{s.label}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {/* Desktop: full dropdown */}
          <div className="hidden sm:block">
            <Select value={statusFilter || 'all'} onValueChange={handleStatusFilter}>
              <SelectTrigger className="w-[180px] h-10" data-testid="filter-status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-2" data-testid="invoices-mobile-cards">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : invoices.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12">
              <Receipt className="w-10 h-10 text-muted-foreground/40" />
              <p className="text-sm font-medium">{search || statusFilter ? 'No invoices found' : 'No invoices yet'}</p>
            </div>
          ) : invoices.map(inv => (
            <div
              key={inv.id}
              className="border rounded-lg p-3.5 active:bg-muted/50"
              onClick={() => navigate(`/invoices/${inv.id}`)}
              data-testid={`invoice-card-${inv.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-sm">{inv.invoice_number}</span>
                    <StatusBadge status={inv.status} />
                    {inv.source === 'maintenance_plan' && (
                      <Badge className="text-[9px] bg-violet-100 text-violet-700 border-violet-200 gap-0.5" variant="outline">
                        <RefreshCw className="w-2.5 h-2.5" /> Maint
                      </Badge>
                    )}
                  </div>
                  <TruncatedTextWithTooltip
                    as="p"
                    text={inv.customer_name || 'No customer'}
                    className="text-xs text-muted-foreground"
                  />
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold">${inv.total.toFixed(2)}</p>
                  {inv.balance_due > 0 && inv.status !== 'void' && inv.status !== 'paid' && (
                    <p className="text-[11px] text-amber-600">Due ${inv.balance_due.toFixed(2)}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table */}
        <div className="border rounded-lg hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="number" onSort={handleSort}>Details</SortableTableHead>
                <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="status" onSort={handleSort} className="w-32 hidden md:table-cell">Status</SortableTableHead>
                <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="total" onSort={handleSort} className="w-28 text-right hidden sm:table-cell">Total</SortableTableHead>
                <TableHead className="w-28 text-right hidden md:table-cell">Balance</TableHead>
                <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="due_date" onSort={handleSort} className="w-28 text-right hidden lg:table-cell">Due Date</SortableTableHead>
                <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="created" onSort={handleSort} className="w-24 text-right hidden lg:table-cell">Created</SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <Receipt className="w-10 h-10 text-muted-foreground/40" />
                      <p className="text-sm font-medium">{search || statusFilter ? 'No invoices found' : 'No invoices yet'}</p>
                      <p className="text-xs text-muted-foreground">{search || statusFilter ? 'Try adjusting your filters.' : 'Create your first invoice to get started.'}</p>
                      {!search && !statusFilter && canEdit && (
                        <Button size="sm" className="mt-2" onClick={() => navigate('/invoices/new')} data-testid="create-invoice-empty-btn"><Plus className="w-4 h-4 mr-1" /> New Invoice</Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map(inv => (
                  <TableRow
                    key={inv.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                    data-testid={`invoice-row-${inv.id}`}
                  >
                    <TableCell>
                      <div className="flex items-baseline gap-2">
                        <span className="text-base font-semibold" data-testid={`invoice-number-${inv.id}`}>{inv.invoice_number}</span>
                        {inv.estimate_id && <Badge variant="outline" className="text-[9px]">Estimate</Badge>}
                        {inv.job_id && <Badge variant="outline" className="text-[9px]">Job</Badge>}
                        {inv.source === 'maintenance_plan' && (
                          <Badge className="text-[9px] bg-violet-100 text-violet-700 border-violet-200 gap-0.5" variant="outline" data-testid={`maint-badge-${inv.id}`}>
                            <RefreshCw className="w-2.5 h-2.5" /> Maintenance Plan
                          </Badge>
                        )}
                        <span className="sm:hidden"><StatusBadge status={inv.status} /></span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {inv.customer_name || <span className="italic">No customer</span>}
                        {inv.sent_at && <span> · Sent {new Date(inv.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <StatusBadge status={inv.status} />
                    </TableCell>
                    <TableCell className="text-right hidden sm:table-cell">
                      <span className="text-sm font-medium">${inv.total.toFixed(2)}</span>
                    </TableCell>
                    <TableCell className="text-right hidden md:table-cell">
                      <span className={`text-sm font-medium ${inv.balance_due > 0 && inv.status !== 'void' ? 'text-amber-600' : inv.status === 'paid' ? 'text-green-600' : 'text-muted-foreground'}`} data-testid={`balance-${inv.id}`}>
                        {inv.status === 'void' ? '-' : `$${inv.balance_due.toFixed(2)}`}
                      </span>
                    </TableCell>
                    <TableCell className="text-right hidden lg:table-cell">
                      <span className="text-xs text-muted-foreground">
                        {inv.due_date ? new Date(inv.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right hidden lg:table-cell">
                      <span className="text-xs text-muted-foreground">
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <ListPagination 
            page={page} 
            pageSize={pageSize} 
            total={total} 
            totalPages={totalPages} 
            showPageSize={false}
            onPageChange={(p) => {
              const params = new URLSearchParams(searchParams);
              params.set('page', String(p));
              setSearchParams(params);
            }} 
          />
        )}
      </div>
      {canEdit && <MobileFAB onClick={() => navigate('/invoices/new')} data-testid="mobile-fab-invoice" />}
    </Layout>
  );
}
